/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState } from "react";
import { useTranslate } from "../../translate";
import { getFormattedAddress } from "../layout/utils";
import classNames from "classnames";
import { useFixHTML } from "./hooks";
import { motion } from "framer-motion";
import { trackOutboundClick } from "../shared/AnalyticsTracker";

function ItineraryStopCard(props) {
    const { coords, order, isActive, stop, className, ...attrs } = props;
    const [isHover, setIsHover] = useState(false);
    const translate = useTranslate();
    const address = getFormattedAddress(stop.address);
    const website = stop.website;
    const googleMapsLink = coords && `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lon}`;
    const phone = stop.phone.yearRoundPhone;
    const isMultiNight = !!stop.multiNight;
    const description = translate(stop, "description");
    const newDescription = useFixHTML(description);
    const relatedContentLink = stop.en.relatedContent.link;
    const relatedContentLabel = stop.en.relatedContent.label;
    const facebookLink = stop.facebookLink;
    const title = translate(stop, "name");
    const imgUrl = stop.image.medium2x;
    const [hasImgError, setHasImgError] = useState(false);
    const onError = () => setHasImgError(true);
    const isFooterVisible = phone || website;
    let tag = "";
    if (isMultiNight) {
        tag = translate("Multi-Night Stop");
    } else if (stop.overnight) {
        tag = translate("Overnight");
    } else if (order === 1) {
        tag = translate("Start");
    } else if (stop.isLastStop) {
        tag = translate("End");
    }

    return (
        <div css={styles.container} className={classNames("trpr-itinerary-stop-card-container", className)} {...attrs}>
            <article css={styles.wrapper} className={classNames("stop-card", { "is-active": isActive })}>
                {website ? (
                    <div css={styles.body}>
                        <header css={styles.header({ hasDescription: !!newDescription })}>
                            <figure css={styles.imgContainer}>
                                {!hasImgError && <img css={styles.img} src={imgUrl} onError={onError} alt={title} />}
                                {hasImgError && (
                                    <div css={styles.imgErrorPlaceholder}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.94 8.11996L7.48 4.65996L8.4 3.64996C8.78 3.23996 9.32 2.99996 9.88 2.99996H14.12C14.68 2.99996 15.22 3.23996 15.59 3.64996L16.83 4.99996H20C21.1 4.99996 22 5.89996 22 6.99996V19C22 19.05 21.99 19.1 21.98 19.16L16.88 14.06C16.96 13.71 17 13.36 17 13C17 10.24 14.76 7.99996 12 7.99996C11.64 7.99996 11.29 8.03996 10.94 8.11996ZM19.78 22.61L18.17 21H4C2.9 21 2 20.1 2 19V6.99996C2 6.40996 2.27 5.87996 2.68 5.50996L1.39 4.21996C1 3.82996 1 3.19996 1.39 2.80996C1.78 2.41996 2.41 2.41996 2.8 2.80996L21.18 21.19C21.57 21.58 21.57 22.21 21.18 22.6C20.8 23 20.17 23 19.78 22.61ZM14.49 17.32L12.99 15.82C12.67 15.92 12.35 16 12 16C10.34 16 9 14.66 9 13C9 12.65 9.08 12.33 9.19 12.02L7.69 10.52C7.25 11.24 7 12.09 7 13C7 15.76 9.24 18 12 18C12.91 18 13.76 17.75 14.49 17.32Z"
                                                fill="#7875F9"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </figure>
                            <div css={styles.details}>
                                <h3 css={styles.title}>{title}</h3>
                                <p css={styles.address}>{address}</p>
                                <div css={styles.tags}>
                                    <span css={styles.order({ isActive })}>{order}</span>
                                    {tag && <span css={styles.tag}>{tag}</span>}
                                    <motion.a
                                        onHoverStart={() => setIsHover(true)}
                                        onHoverEnd={() => setIsHover(false)}
                                        target="_blank"
                                        href={website}
                                        rel="noopener"
                                        style={{ textDecoration: "none" }}
                                        onClick={() => trackOutboundClick("tripper.app", website)}
                                    >
                                        <span css={styles.learnMore({ isHover, isActive })}>
                                            <span css={styles.learnMoreText({ isActive, isHover })}>
                                                {translate("Learn more")}
                                            </span>
                                            <div css={styles.arrowIconCSS({ isActive, isHover })}>
                                                <i className="trpr__icon-forward placeholder"></i>
                                                <i className="trpr__icon-forward unit"></i>
                                            </div>
                                        </span>
                                    </motion.a>
                                </div>
                            </div>
                        </header>
                        <div css={styles.miniMobileTags}>
                            <span css={styles.order({ isActive })}>{order}</span>
                            {tag && <span css={styles.tag}>{tag}</span>}
                            <motion.a
                                onHoverStart={() => setIsHover(true)}
                                onHoverEnd={() => setIsHover(false)}
                                target="_blank"
                                href={website}
                                rel="noopener"
                                style={{ textDecoration: "none" }}
                                onClick={() => trackOutboundClick("tripper.app", website)}
                            >
                                <span css={styles.learnMore({ isHover, isActive })}>
                                    <span css={styles.learnMoreText({ isActive, isHover })}>
                                        {translate("Learn more")}
                                    </span>
                                    <div css={styles.arrowIconCSS({ isActive, isHover })}>
                                        <i className="trpr__icon-forward placeholder"></i>
                                        <i className="trpr__icon-forward unit"></i>
                                    </div>
                                </span>
                            </motion.a>
                        </div>
                        {newDescription && (
                            <div css={styles.description} dangerouslySetInnerHTML={{ __html: newDescription }} />
                        )}
                        {relatedContentLink && relatedContentLabel && (
                            <div style={{ marginTop: "10px" }}>
                                <div style={{ fontStyle: "bold", fontSize: "8px" }}>Related content:</div>
                                <a href={relatedContentLink} target="_blank">
                                    {relatedContentLabel}
                                    <i className="trpr__icon-forward unit"></i>
                                </a>
                            </div>
                        )}
                        {translate(stop.multiNight, "description") && (
                            <div className="trpr-itinerary-stop-card__multi-night">
                                <i className="trpr__icon-overnight"></i>
                                <div
                                    className="trpr-itinerary-stop-card__multi-night-body"
                                    dangerouslySetInnerHTML={{ __html: translate(stop.multiNight, "description") }}
                                ></div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div css={styles.body}>
                        <header css={styles.header({ hasDescription: !!newDescription })}>
                            <figure css={styles.imgContainer}>
                                {!hasImgError && <img css={styles.img} src={imgUrl} onError={onError} alt={title} />}
                                {hasImgError && (
                                    <div css={styles.imgErrorPlaceholder}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.94 8.11996L7.48 4.65996L8.4 3.64996C8.78 3.23996 9.32 2.99996 9.88 2.99996H14.12C14.68 2.99996 15.22 3.23996 15.59 3.64996L16.83 4.99996H20C21.1 4.99996 22 5.89996 22 6.99996V19C22 19.05 21.99 19.1 21.98 19.16L16.88 14.06C16.96 13.71 17 13.36 17 13C17 10.24 14.76 7.99996 12 7.99996C11.64 7.99996 11.29 8.03996 10.94 8.11996ZM19.78 22.61L18.17 21H4C2.9 21 2 20.1 2 19V6.99996C2 6.40996 2.27 5.87996 2.68 5.50996L1.39 4.21996C1 3.82996 1 3.19996 1.39 2.80996C1.78 2.41996 2.41 2.41996 2.8 2.80996L21.18 21.19C21.57 21.58 21.57 22.21 21.18 22.6C20.8 23 20.17 23 19.78 22.61ZM14.49 17.32L12.99 15.82C12.67 15.92 12.35 16 12 16C10.34 16 9 14.66 9 13C9 12.65 9.08 12.33 9.19 12.02L7.69 10.52C7.25 11.24 7 12.09 7 13C7 15.76 9.24 18 12 18C12.91 18 13.76 17.75 14.49 17.32Z"
                                                fill="#7875F9"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </figure>
                            <div css={styles.details}>
                                <h3 css={styles.title}>
                                    <span css={[styles.order({ isActive }), { fontWeight: 100 }]}>{order}</span> {title}
                                </h3>
                                <p css={styles.address}>{address}</p>
                                <div css={styles.description} dangerouslySetInnerHTML={{ __html: newDescription }} />
                            </div>
                        </header>
                        {translate(stop.multiNight, "description") && (
                            <div className="trpr-itinerary-stop-card__multi-night">
                                <i className="trpr__icon-overnight"></i>
                                <div
                                    className="trpr-itinerary-stop-card__multi-night-body"
                                    dangerouslySetInnerHTML={{ __html: translate(stop.multiNight, "description") }}
                                ></div>
                            </div>
                        )}
                    </div>
                )}
                {isFooterVisible && (
                    <footer css={styles.footer({ hasPhone: !!phone, hasWebsite: !!website })}>
                        <ul>
                            {phone && (
                                <li>
                                    <a
                                        onClick={() => trackOutboundClick("tripper.app", phone)}
                                        data-disabled={!phone}
                                        href={`tel:${phone}`}
                                    >
                                        {phone}
                                    </a>
                                </li>
                            )}
                            {/*{website && !facebookLink && (*/}
                            {/*    <li className="website">*/}
                            {/*        /!* eslint-disable-next-line *!/*/}
                            {/*        <a*/}
                            {/*            data-disabled={!website}*/}
                            {/*            target="_blank"*/}
                            {/*            href={website}*/}
                            {/*            rel="noopener"*/}
                            {/*            aria-label={translate("Visit Website")}*/}
                            {/*        />*/}
                            {/*    </li>*/}
                            {/*)}*/}
                            {website && facebookLink && (
                                <li className="facebook">
                                    {/* eslint-disable-next-line */}
                                    <a
                                        onClick={() => trackOutboundClick("tripper.app", facebookLink)}
                                        data-disabled={!facebookLink}
                                        target="_blank"
                                        href={facebookLink}
                                        rel="noopener"
                                        aria-label={translate("Facebook")}
                                    />
                                </li>
                            )}
                            {!website && facebookLink && (
                                <li className="facebook">
                                    {/* eslint-disable-next-line */}
                                    <a
                                        data-disabled={!facebookLink}
                                        target="_blank"
                                        href={facebookLink}
                                        rel="noopener"
                                        aria-label={translate("Facebook")}
                                        onClick={() => trackOutboundClick("tripper.app", facebookLink)}
                                    />
                                </li>
                            )}
                            <li>
                                <a
                                    data-disabled={!googleMapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={googleMapsLink}
                                    onClick={() => trackOutboundClick("tripper.app", googleMapsLink)}
                                >
                                    {translate("Google Maps")}
                                </a>
                            </li>
                        </ul>
                    </footer>
                )}
            </article>
        </div>
    );
}

const styles = {
    header: ({ hasDescription }) => css`
        margin: 0;
        display: flex;
        align-items: center;

        ${hasDescription &&
            `
            margin: 0 0 16px;
        `}

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            //display: flex;
            //align-items: center;
        }
    `,
    imgContainer: css`
        width: 100px;
        height: 100px;
        margin: 0 16px 0 0;
        align-self: flex-start;
        flex: none;

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            align-self: flex-start;
            width: 140px;
            height: 140px;
            flex: none;
            margin: 0 24px 0 0;
        }
    `,
    img: css`
        background-color: #d3d2fc;
        border-radius: 4px;
        height: 100%;
        object-fit: cover;
        overflow: hidden;
        text-indent: 100%;
        text-indent: 200px;
        white-space: nowrap;
        width: 100%;
    `,
    imgErrorPlaceholder: css`
        align-items: center;
        background-color: #d3d2fc;
        border-radius: 4px;
        display: flex;
        height: 100%;
        justify-content: center;
        width: 100%;
    `,
    container: css`
        --padding-x: 16px;
        padding-left: var(--padding-x);
        padding-right: var(--padding-x);

        .trpr__primary-layout-itinerary-items[data-min-width~="450"] & {
            --padding-x: 24px;
        }

        .trpr__primary-layout-itinerary-items[data-min-width~="650"] & {
            --padding-x: 36px;
        }
    `,
    wrapper: css`
        border-radius: 8px;
        box-shadow: 0px 2px 6px 0px rgba(30, 22, 53, 0.35);
        background: #ffffff;
        overflow: hidden;
    `,
    body: css`
        display: block;
        padding: 16px;
        text-decoration: none !important;

        strong,
        b {
            font-weight: 700;
        }

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            padding: 24px;
        }
    `,
    title: css`
        color: #505050;
        font-size: 16px;
        line-height: 24px;
        text-transform: none;
        font-family: "Lexend Deca", sans-serif;
        margin: 0 0 8px;

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            font-size: 18px;
            line-height: 24px;
        }
    `,
    address: css`
        font-size: 13px;
        line-height: 20px;
        color: #999999;
        margin: 0 0 16px;

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            font-size: 15px;
            line-height: 24px;
            margin: 0 0 12px;
        }
    `,
    miniMobileTags: css`
        display: grid;
        grid-template-columns: auto auto auto 1fr;
        column-gap: 8px;
        font-weight: 400;
        margin-bottom: 10px;
        @media (min-width: 415px) {
            display: none;
        }
    `,
    tags: css`
        display: none;
        @media (min-width: 415px) {
            display: grid;
            grid-template-columns: auto auto auto 1fr;
            column-gap: 8px;
            font-weight: 400;
        }
    `,
    order: ({ isActive }) => css`
        align-items: center;
        background: #e8e8ff;
        border-radius: 4px;
        color: #504cf3;
        display: inline-flex;
        font-size: 12px;
        font-weight: 600;
        height: 30px;
        justify-content: center;
        line-height: 30px;
        width: 30px;
        transition: all 200ms ease-out;

        ${isActive &&
            `
            background: #504CF3;
            color: #ffffff;
        `}
    `,
    tag: css`
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 12px;
        background: #cae9e1;
        color: #505050;
        font-weight: 600;
        font-size: 13px;
        line-height: 20px;
        border-radius: 4px;
        white-space: nowrap;
    `,
    learnMore: ({ isActive, isHover }) => css`
        align-items: center;
        border-radius: 4px;
        border: 1px solid #6d9d92;
        display: inline-flex;
        height: 30px;
        padding: 0;
        justify-content: center;
        color: #6d9d92;
        transition: all 200ms ease-out;
        width: 92px;

        ${(isActive || isHover) &&
            `
            width: 114px;
        `}

        ${(isActive || isHover) &&
            `
            color: #ffffff;
            background: #6d9d92;
        `}
    `,
    learnMoreText: ({ isHover, isActive }) => css`
        color: inherit;
        font-weight: 600;
        font-size: 13px;
        line-height: 20px;
        transform: translateX(11px);
        transition: all 200ms ease-out;
        white-space: nowrap;

        ${(isActive || isHover) &&
            `
            transform: translateX(0);
        `}
    `,
    arrowIconCSS: ({ isActive, isHover }) => css`
        position: relative;
        margin-left: 6px;
        display: flex;
        align-items: center;
        color: inherit;

        .placeholder {
            opacity: 0;
            position: relative;
            color: inherit;
        }

        .unit {
            opacity: 0;
            transition: all 200ms ease-out;
            position: absolute;
            top: 0;
            left: 0;
            transform: translateX(11px);
            color: inherit;

            ${(isActive || isHover) &&
                `
                transform: translateX(0);
                opacity: 1;
            `}
        }
    `,
    description: css`
        font-weight: 300;
        font-size: 13px;
        line-height: 20px;
        color: #505050;

        a {
            color: #504cf3;
        }

        p {
            margin-top: 0;
            margin-bottom: 0;

            & + p {
                margin-top: 1em;
            }
        }

        .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
            font-size: 15px;
            line-height: 24px;
        }
    `,
    footer: ({ hasPhone, hasWebsite }) => css`
        --border-color: #d3d2fc;

        ul {
            display: grid;
            grid-template-columns: 1fr 0.6fr 1fr;
            border-top: 1px solid var(--border-color);
            transition: all 200ms ease-out;

            ${(!hasPhone || !hasWebsite) &&
                `
                grid-template-columns: 1fr 1fr;
            `}

            .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
                grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
            }

            // HACK: fix whitespace between items
            .stop-card.is-active & {
                .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
                    background: #ededff;
                }
            }
        }

        li {
            &:not(:last-child) {
                border-right: 1px solid var(--border-color);
            }

            &.facebook {
                /* TODO: remove HACK */
                a::after {
                    content: "Facebook";
                    transition: all 200ms ease-out;
                    display: block;

                    .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
                        content: "Facebook";
                    }
                }
            }

            &.website {
                /* TODO: remove HACK */
                a::after {
                    content: "Website";
                    transition: all 200ms ease-out;
                    display: block;

                    .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
                        content: "Visit Website";
                    }
                }
            }
        }

        a {
            align-items: center;
            color: #999999;
            display: flex;
            font-size: 12px;
            height: 36px;
            justify-content: center;
            line-height: 20px;
            text-align: center;
            text-decoration: none !important;
            width: 100%;
            transition: all 200ms ease-out;

            &:hover:not([data-disabled="true"]) {
                background: #ededff;
                color: #7875f9;
            }

            &[data-disabled="true"] {
                pointer-events: none;
                opacity: 0.5;
            }

            .trpr__primary-layout-itinerary-items[data-min-width~="545"] .stop-card.is-active & {
                color: #7875f9;
            }

            .trpr__primary-layout-itinerary-items[data-min-width~="545"] & {
                height: 48px;
                font-size: 15px;
                line-height: 24px;
            }
        }
    `,
};

export default ItineraryStopCard;
